import { addDays, type DateRange } from "@prismicio/editor-support/Date"
import { Box, SegmentedProgressBar, Skeleton } from "@prismicio/editor-ui"

import { useSubscriptionQuery } from "../billing/useSubscription"
import { usageSummaryItemTypes } from "./usageSummaryItemTypes"
import { useUsageQuery } from "./useUsage"

interface UsageProgressBarProps {
  itemTypeKey: keyof typeof usageSummaryItemTypes
  selectedDateRange: DateRange
  planLimitsKnown: boolean
}

export function UsageProgressBar(props: UsageProgressBarProps) {
  const { itemTypeKey, selectedDateRange, planLimitsKnown } = props

  const { data: subscription } = useSubscriptionQuery()
  const { data: usage } = useUsageQuery(
    selectedDateRange.startDate,
    addDays(selectedDateRange.endDate, 1),
  )

  if (!planLimitsKnown) return null

  const itemType = usageSummaryItemTypes[itemTypeKey]
  const quota = itemType.getQuota(subscription)
  const limit = itemType.getLimit(subscription)

  if (limit === "unlimited" || quota === "unlimited") return null

  const totalUsage = itemType.getTotalUsage(usage)

  return (
    <SegmentedProgressBar
      threshold={quota}
      max={limit}
      value={totalUsage}
      belowThresholdLabel="Included quota"
      aboveThresholdLabel="Allowed overage"
    />
  )
}

interface UsageProgressBarSkeletonProps {
  planLimitsKnown?: boolean
}

export function UsageProgressBarSkeleton(props: UsageProgressBarSkeletonProps) {
  const { planLimitsKnown = true } = props
  return planLimitsKnown ? (
    <Box gap={16} alignItems="center" flexDirection="column">
      <Skeleton width="100%" height={16} color="green4" />
      <Skeleton width={120} height={16} color="grey5" />
    </Box>
  ) : null
}
